import React, { useEffect } from 'react'
import LabeledCounter from 'src/shared/components/forms/counter/LabeledCounter'
import FormSection from 'src/shared/components/forms/FormSection/FormSection'
import FormSectionRow from 'src/shared/components/forms/FormSectionRow/FormSectionRow'
import './Principal.css'
import { ExtensionEstanciaValues } from '../../EstensionEstancia-types'
import { Controller, useFormContext } from 'react-hook-form'
import LabelTitle from 'src/shared/components/data-display/LabelTitle/LabelTitle'
import { Cama, Habitacion, ModalidadGrupoTarifa, Tarifa, TipoAlojamientoGrupoTarifa, TiposAlojamientos, TipoTarifa, useGruposTarifasRenovacionEstanciaQuery } from 'src/gql/schema'
import InputTarifaBase from './sections/InputTarifaBase/InputTarifaBase'
import InputTarifaAdicional from './sections/InputTarifaAdicional/InputTarifaAdicional'
import { useCosts } from '../../context/CostContext'
import { useProfile } from 'src/shared/hooks/useProfile'

const Principal = ({
    tipo_alojamiento,
    tipo_habitacion_id,
    tipo_habitacion_nombre,
    tipo_habitacion_camas,
    tipo_habitacion_personas_incluidas,
    habitacion,
    loading
}: {
    tipo_alojamiento?: TiposAlojamientos,
    tipo_habitacion_id?: string
    tipo_habitacion_nombre?: string,
    tipo_habitacion_camas?: Cama[],
    tipo_habitacion_personas_incluidas?: number,
    habitacion?: Habitacion,
    loading?: boolean
}) => {

    const {
        control,
        watch,
        trigger,
        formState,
    } = useFormContext<ExtensionEstanciaValues>()
    const { setTarifaBaseHabitacion, tarifaBaseHabitacion } = useCosts()
    
    const { hotel_id } = useProfile()

    const nochesExtra = watch("principal.nochesExtraCount")
    const horasExtra = watch("principal.horasExtraCount")

    useEffect(() => {
        if (formState.isSubmitted) {
            trigger("principal.nochesExtraCount")
            trigger("principal.horasExtraCount")
        }
    }, [nochesExtra, horasExtra])

    const { data: gruposTarifasBase } = useGruposTarifasRenovacionEstanciaQuery({
        variables: {
            hotel_id,
            tipo_habitacion_id: tipo_habitacion_id!,
            tipo_alojamiento: tipo_alojamiento as unknown as TipoAlojamientoGrupoTarifa,
            tipos_tarifa: [TipoTarifa.Renta, TipoTarifa.RentaReserva],
            modalidades: [ModalidadGrupoTarifa.Base]
        },
        skip: !tipo_habitacion_id || !tipo_alojamiento
    })

    useEffect(() => {
        const isMotel = tipo_alojamiento === TiposAlojamientos.Motel
        const tarifaBaseAll = gruposTarifasBase?.grupos_tarifas.flatMap(gp => gp.tarifas || [])
        const tarifaBaseMotelId = isMotel
            ? habitacion?.ultima_renta?.tarifas?.find(t => t.grupo_tarifa?.modalidad === ModalidadGrupoTarifa.Base)?.tarifa_id
            : undefined
        const tarifaBase = (isMotel && tarifaBaseMotelId)
            ? tarifaBaseAll?.find(t => t.tarifa_id === tarifaBaseMotelId) ?? tarifaBaseAll?.[0]
            : tarifaBaseAll?.[0]
        if (tarifaBase) {
            setTarifaBaseHabitacion(tarifaBase as Tarifa)
        }
    }, [habitacion, gruposTarifasBase])

    return (
        <>
            <div className="extension-estancia__header-labels">
                <LabelTitle
                    icon="openDoor"
                    text={`Habitación ${tipo_habitacion_nombre || ""}`}
                />
                {tipo_habitacion_camas?.map((c, index) => (
                    <LabelTitle
                        key={index}
                        icon="BedFilled"
                        text={`${c.numero || ""} cama(s) ${c.tipo}`}
                    />
                ))}
                <LabelTitle
                    icon="UserParentFill"
                    text={`${tipo_habitacion_personas_incluidas || "-"} personas`}
                />
                <LabelTitle
                    icon="userAdd"
                    text={`Máximo ${tarifaBaseHabitacion?.tipo_habitacion?.personas_extra_max || 0} personas extra`}
                />
            </div>
            <FormSection title="">
                <FormSectionRow style={{ marginBottom: "24px" }}>
                    <Controller
                        control={control}
                        name='principal.nochesExtraCount'
                        rules={{ validate: () => !!horasExtra || !!nochesExtra }}
                        render={({ field: { onChange, value }, fieldState: { error } }) =>
                            <LabeledCounter
                                disable={loading}
                                style={{ width: 180 }}
                                rightLabel={tipo_alojamiento === TiposAlojamientos.Hotel ? 'Noches extra' : "Hospedajes extra"} 
                                errorHintText={error?.type === "validate" ? "Campo requerido" : habitacion?.tipo_habitacion?.hospedajes_extra_max === null ? "" : ((
                                    habitacion?.tipo_habitacion?.hospedajes_extra_max || 0
                                )) < (value || 0) ? "Límite de estancias extra superado, necesitarás PIN para vender" : ""}
                                onClick={onChange}
                                value={value}
                                min={0}
                            />
                        }
                    />
                    <Controller
                        control={control}
                        name='principal.horasExtraCount'
                        rules={{ validate: () => !!horasExtra || !!nochesExtra }}
                        render={({ field: { onChange, value }, fieldState: { error } }) =>
                            <LabeledCounter
                                style={{ width: 180 }}
                                rightLabel='Horas extra'
                                errorHintText={error?.type === "validate" ? "Campo requerido" : tarifaBaseHabitacion?.tipo_habitacion?.horas_extra_max === null ? "" : ((
                                    habitacion?.tipo_habitacion?.horas_extra_max || 0
                                )) < (value || 0) ? "Límite de horas extra superado, necesitarás PIN para vender" : ""}
                                onClick={onChange}
                                disable={loading}
                                value={value}
                                min={0}
                            />
                        }
                    />
                </FormSectionRow>
                <FormSectionRow>
                    <InputTarifaBase
                        tipo_habitacion_id={tipo_habitacion_id}
                        tipo_alojamiento={tipo_alojamiento}
                        habitacion={habitacion}
                        loading={loading}
                    />
                    <InputTarifaAdicional
                        tipo_habitacion_id={tipo_habitacion_id}
                        tipo_alojamiento={tipo_alojamiento}
                        habitacion={habitacion}
                        loading={loading}
                    />
                </FormSectionRow>
            </FormSection>
        </>
    )
}

export default Principal